// NOSONAR:S3358
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { Store } from '@ngrx/store';
import { filter, map, startWith } from 'rxjs/operators';
import { Crop, PhosphateCondition, PhosphateLegalNorm, SiteState } from 'src/app/shared/models';

import { PhosphateLegalNormDialogModel } from './phosphate-legal-norm-dialog.model';
import { DisplayService } from '../../../shared/services/display.service';

@Component({
  selector: 'app-phosphate-legal-norm-dialog',
  templateUrl: './phosphate-legal-norm-dialog.component.html',
  standalone: false,
})
export class PhosphateLegalNormDialogComponent extends AbstractFormDialog<PhosphateLegalNorm> implements OnInit {
  data = inject<PhosphateLegalNormDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef = inject<MatDialogRef<PhosphateLegalNormDialogComponent, AbstractDialogResultData<PhosphateLegalNorm>>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  displayService = inject(DisplayService);
  protected store = inject<Store<SiteState>>(Store);

  formGroup = this.fb.group({
    id: [this.data.phosphateLegalNorm.id],
    calendarYear: [this.data.phosphateLegalNorm.calendarYear, [Validators.required]],
    cropId: [this.data.phosphateLegalNorm.cropId],
    pCaClFromAndIncluding: [this.data.phosphateLegalNorm.pCaClFromAndIncluding],
    pCaClToAndIncluding: [this.data.phosphateLegalNorm.pCaClToAndIncluding],
    pAlFromAndIncluding: [this.data.phosphateLegalNorm.pAlFromAndIncluding],
    pAlToAndIncluding: [this.data.phosphateLegalNorm.pAlToAndIncluding],
    pwFromAndIncluding: [this.data.phosphateLegalNorm.pwFromAndIncluding],
    pwToAndIncluding: [this.data.phosphateLegalNorm.pwToAndIncluding],
    condition: [this.data.phosphateLegalNorm.condition, [Validators.required]],
    phosphateNorm: [this.data.phosphateLegalNorm.phosphateNorm, [Validators.required]],
    poorInPhosphateNorm: [this.data.phosphateLegalNorm.poorInPhosphateNorm, [Validators.required]],
    isTransitionVariant: [this.data.phosphateLegalNorm.isTransitionVariant, [Validators.required]],
  });

  filteredCrops$ = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(''),
    filter((value) => typeof value === 'string'),
    map((value: string) => this.removeDuplicateCrops([...this.filterCrop(value, [...this.data.crops])])),
  );

  cropId$ = this.formGroup.controls.cropId.valueChanges.pipe(
    startWith(this.data.phosphateLegalNorm.cropId),
    filter((value) => typeof value === 'number'),
  );
  cropId: number;

  conditions = PhosphateCondition;

  ngOnInit(): void {
    this.cropId$.subscribe((cropId) => {
      this.cropId = cropId;
    });
  }

  isNewPhosphateLegalNorm() {
    return !this.data.phosphateLegalNorm.id;
  }

  confirm() {
    super.confirm({
      ...this.data.phosphateLegalNorm,
      ...this.formGroup.getRawValue(),
      agrifirmCropId: this.getCrop(this.formGroup.get('cropId').value)?.agrifirmCropId,
    });
  }

  getCrop = (cropId?: number) => (cropId ? this.data.crops.find((crop) => crop.id === cropId) : null);

  displayCrop() {
    return (cropId?: number) => this.getCrop(cropId)?.name ?? '';
  }

  getConditionString = (condition: string): string => this.displayService.getPhosphateConditionString(condition);

  private filterCrop(value: string, items: Crop[]) {
    return [...items]
      .sort((a, b) => {
        if (!(a.name && b.name)) {
          return 0;
        }
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      })
      .filter((option) => {
        if (!option.name) {
          return false;
        }
        return option.name.toLowerCase().includes(value.toLowerCase());
      });
  }

  private removeDuplicateCrops(items: Crop[]): Crop[] {
    return items.filter((v, i, a) => a.findIndex((t) => (!t?.id || !v?.id ? -1 : t.id === v.id)) === i);
  }
}
